import { apiClient } from './client';
import type { Anfrage, AuditLogEntry, StateInfo } from '../types';
import type { PaginatedResponse } from '../types/api';

export type AnfrageListResponse = PaginatedResponse<Anfrage>;

export interface AnfrageCreateData {
  strasse: string;
  hausnummer: string;
  plz?: string;
  city?: string;
  notes?: string;
  responsible_person?: string;
}

export interface AnfrageUpdateData {
  adresse_id?: number | null;
  haus_id?: number | null;
  einheit_id?: number | null;
  verfahren_id?: number | null;
  vermieter_id?: number | null;
  adresse_text?: string;
  zipcode?: string;
  city?: string;
  address_verified?: boolean;
  notes?: string;
  responsible_person?: string;
}

export interface AnfrageQueryParams {
  page?: number;
  per_page?: number;
  search_text?: string;
  status?: string;
  adresse_id?: number;
  haus_id?: number;
  einheit_id?: number;
  verfahren_id?: number;
  sort_by?: string;
  sort_dir?: 'asc' | 'desc';
  include_hidden?: boolean;
}

export const anfragenApi = {
  getAnfragen: async (params: AnfrageQueryParams = {}): Promise<AnfrageListResponse> => {
    const { data } = await apiClient.get('/anfragen/', { params });
    return data;
  },

  getAnfrage: async (id: number): Promise<Anfrage> => {
    const { data } = await apiClient.get(`/anfragen/${id}`);
    return data;
  },

  createAnfrage: async (body: AnfrageCreateData): Promise<Anfrage> => {
    const { data } = await apiClient.post('/anfragen/', body);
    return data;
  },

  updateAnfrage: async (id: number, updates: AnfrageUpdateData): Promise<Anfrage> => {
    const { data } = await apiClient.patch(`/anfragen/${id}`, updates);
    return data;
  },

  deleteAnfrage: async (id: number): Promise<void> => {
    await apiClient.delete(`/anfragen/${id}`);
  },

  getAnfragenAtAdresse: async (adresseId: number): Promise<Anfrage[]> => {
    const { data } = await apiClient.get(`/adressen/${adresseId}/anfragen`);
    return data;
  },

  getAnfragenAtHaus: async (hausId: number): Promise<Anfrage[]> => {
    const { data } = await apiClient.get(`/haeuser/${hausId}/anfragen`);
    return data;
  },

  getAnfragenAtEinheit: async (einheitId: number): Promise<Anfrage[]> => {
    const { data } = await apiClient.get(`/einheiten/${einheitId}/anfragen`);
    return data;
  },

  createVerfahren: async (anfrageId: number, body: { aktenzeichen?: string; notes?: string }): Promise<{ id: number }> => {
    const { data } = await apiClient.post(`/anfragen/${anfrageId}/verfahren`, body);
    return data;
  },

  getStates: async (): Promise<StateInfo[]> => {
    const { data } = await apiClient.get('/anfragen/states');
    return data;
  },

  changeState: async (
    id: number,
    new_state: string,
    comment?: string,
    handoff?: { assignee_user_id: number; assignment_context: string },
  ): Promise<Anfrage> => {
    const { data } = await apiClient.patch(`/anfragen/${id}/state`, {
      new_state,
      comment,
      ...(handoff ? handoff : {}),
    });
    return data;
  },

  getHistory: async (id: number): Promise<AuditLogEntry[]> => {
    const { data } = await apiClient.get(`/anfragen/${id}/verlauf`);
    return data;
  },
};
